'use client'

import type { ClientEvents } from '@mentora/data-access/analytics-events'
import { useEffect, useRef } from 'react'
import { useAnalytics } from './use-analytics'

/**
 * Track `event` once per `key` (e.g. a course id), on mount: page views of a
 * thing, as opposed to `$pageview`. `properties` are read when it fires, so
 * they need not be memoised; pass null to hold off until they are known.
 */
export function useTrackOnce<E extends keyof ClientEvents>(event: E, properties: ClientEvents[E] | null, key: string): void {
  const { track } = useAnalytics()
  const latest = useRef(properties)
  latest.current = properties
  const tracked = useRef<string | null>(null)

  useEffect(() => {
    if (tracked.current === key || !latest.current)
      return
    tracked.current = key
    track(event, latest.current)
  }, [event, key, track])
}
